import { useState, useEffect, useCallback } from 'react';
import firebase, { db } from 'utils/firebase';
import logFirestoreError from 'utils/logFirestoreError';

type Type<T = any> = [
  {
    ref: any;
    data: T | null;
    loading: boolean;
    error: null | string;
    exists: boolean;
  },
  React.Dispatch<React.SetStateAction<T | null>>
];

function useDocument<T = any>(
  collectionPath: string = '',
  id: string | null,
  editDocOnSet = false,
  // Skip logging expected permission-denied errors. Some docs (e.g. a
  // restaurant's credentials/* subcollection) are intentionally readable only
  // by privileged roles, so a denial for everyone else is expected noise, not a
  // bug. The error is still surfaced via the returned `error` state.
  silenceExpectedPermissionErrors = false
): Type<T> {
  const [data, setdata] = useState<T | null>(null);
  const [loading, setloading] = useState<boolean>(true);
  const [error, seterror] = useState<null | string>(null);
  const [exists, setexists] = useState<boolean>(true);

  let ref: any =
    collectionPath && !collectionPath.includes('//') && !!id
      ? db.collection(collectionPath).doc(id)
      : null;

  useEffect(() => {
    let unsubscribe: undefined | (() => void);
    // Each setter below renders synchronously, and that render can run this
    // effect's cleanup while the callback is still going.
    let active = true;

    if (!id) {
      setdata(null);
      setloading(false);
    } else if (ref) {
      setloading(true);
      setdata(null);
      seterror(null);

      unsubscribe = ref?.onSnapshot(
        (querySnapshot: firebase.firestore.DocumentSnapshot) => {
          setdata((querySnapshot?.data() ?? null) as T);
          if (!active) return;
          setloading(false);
          if (!active) return;
          setexists(!!querySnapshot.exists);
        },
        (err: firebase.firestore.FirestoreError) => {
          const isExpectedPermissionError =
            silenceExpectedPermissionErrors &&
            err.code === 'permission-denied';
          if (!isExpectedPermissionError) {
            logFirestoreError('useDocument', `${collectionPath}/${id}`, err);
          }
          seterror(err.message);
          if (!active) return;
          setloading(false);
        }
      );
    }
    // Unsubscribe the subscription created by THIS effect run. React invokes
    // this cleanup before re-running on dep change and on unmount, so holding
    // the active unsubscribe in the closure avoids leaking listeners (the old
    // `listener` state was set after the effect ran and read a stale value).
    return () => {
      active = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [db, collectionPath, id, silenceExpectedPermissionErrors]);

  const setData: React.Dispatch<React.SetStateAction<T | null>> = useCallback(
    (fn: T | null | ((d: T | null) => T | null)) => {
      if (!editDocOnSet) {
        setdata(fn);
      } else {
        let d =
          typeof fn === 'function' ? (fn as (d: T | null) => T)(data) : fn;

        if (d) {
          ref.set(d, { merge: true });
        }
      }
    },
    [ref, editDocOnSet, data]
  );

  return [{ ref, data, loading, error, exists }, setData];
}

export default useDocument;
